import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaComments } from "react-icons/fa";

// const API_BASE = "https://hopeful-cosmic-koala.ngrok-free.app/api";
const API_BASE = "/api";

const Events = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState([]);  // Initialize as empty array
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const filters = ["All", "Upcoming", "Active", "Finished"];

  // Fetch events from API
  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`${API_BASE}/events/`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': '69420',
          },
        });
        
      // ✅ If request fails, DO NOT throw error → just show empty state
      if (!response.ok) {
        console.warn(`Request failed: ${response.status}`);
        setEvents([]);
        return;
      }

          // ✅ Check if response is JSON
      const contentType = response.headers.get("content-type");

      if (!contentType || !contentType.includes("application/json")) {
        console.warn("Response is not JSON. Showing empty events.");
        setEvents([]);
        return;
      }

      const data = await response.json();

      console.log('Events API response:', data);

      // ✅ Handle all formats safely
      if (Array.isArray(data)) {
        setEvents(data);
      } else if (data && Array.isArray(data.results)) {
        setEvents(data.results);
      } else if (data && typeof data === 'object') {
        setEvents([data]);
      } else {
        setEvents([]);
      }

    } catch (err) {
      console.warn('Fetch error (handled silently):', err);

      // ✅ DO NOT set error → avoids "Could not load events"
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  fetchEvents();
}, []);
  // ... rest of your component stays the same

  // Helper to extract plain text from Quill Delta JSON
const extractDetailsText = (detailsJson) => {
  try {
    if (!detailsJson) return '';
    
    // Already an object from the API, no need to parse
    const delta = typeof detailsJson === 'string' 
      ? JSON.parse(detailsJson) 
      : detailsJson;

    if (!delta?.ops) return '';

    return delta.ops
      .filter(op => typeof op.insert === 'string') // ignore image/embed ops
      .map(op => op.insert)
      .join('')
      .replace(/\n+/g, ' ')
      .trim();
  } catch {
    return '';
  }
};

  const filteredEvents = events.filter(event => {
    const matchesFilter = activeFilter === "All" || event.status_display === activeFilter;
    const details = extractDetailsText(event.detailsJson);
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.location_address.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         details.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const openEventModal = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const closeEventModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getStatusBadgeClass = (status) => {
    switch(status) {
      case "Upcoming": return "bg-primary";
      case "Active": return "bg-success";
      case "Finished": return "bg-secondary";
      default: return "bg-primary";
    }
  };

  useEffect(() => {
    if (showEventModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showEventModal]);

  if (loading) {
    return (
      <>
        <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet" />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh' 
        }}>
          <div style={{ 
            width: '44px', 
            height: '44px', 
            border: '4px solid #e8f4fd', 
            borderTopColor: '#0b1c6b', 
            borderRadius: '50%', 
            animation: 'spin 0.8s linear infinite' 
          }}></div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet" />
        <div className="container" style={{ textAlign: 'center', padding: '80px 20px' }}>
          <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>⚠️</div>
          <h4 style={{ fontSize: '1.1rem', color: '#0b1c6b', marginBottom: '8px' }}>
            Could not load events
          </h4>
          <p style={{ color: '#6b7280' }}>{error}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css" />

      <div className="events-page">
        {/* HERO SECTION */}
        <section className="events-hero">
          <div className="hero-overlay">
            <div className="container">
              <div className="row align-items-center">
                <div className="col-lg-6 text-white mb-4 mb-lg-0">
                  <h1 className="display-3 fw-bold mb-3">Church Events</h1>
                  <p className="fs-5 mb-4">Come, let's share God's love.</p>
                  <p className="lead mb-0">
                  </p>
                </div>
                <div className="col-lg-6">
                  <div className="search-container">
                    <div className="input-group input-group-lg shadow-lg">
                      <span className="input-group-text bg-white border-0">
                        <i className="bi bi-search text-primary fs-5"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control border-0"
                        placeholder="Search events by name, location..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FILTERS */}
        <section className="py-4 bg-white sticky-top shadow-sm" style={{ top: '0', zIndex: '1020' }}>
          <div className="container">
            <div className="d-flex flex-wrap gap-2 justify-content-center">
              {filters.map((filter) => (
                <button
                  key={filter}
                  className={`btn ${activeFilter === filter ? 'btn-primary' : 'btn-outline-primary'} rounded-pill px-4`}
                  onClick={() => setActiveFilter(filter)}
                >
                  <i className="bi bi-calendar-event me-2"></i>
                  {filter}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* EVENTS GRID */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="row mb-4">
              <div className="col">
                <p className="text-muted mb-0">
                  <i className="bi bi-calendar-check me-2"></i>
                  Showing {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
                  {activeFilter !== "All" && ` - ${activeFilter}`}
                </p>
              </div>
            </div>

            {filteredEvents.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-inbox fs-1 text-muted mb-3 d-block"></i>
                <h5 className="text-muted">No events found</h5>
                <p className="text-muted">Try adjusting your search or filter</p>
              </div>
            ) : (
              <div className="row g-4">
                {filteredEvents.map((event) => {
                  const details = extractDetailsText(event.detailsJson);
                  return (
                    <div className="col-lg-4 col-md-6" key={event.id}>
                      <div 
                        className="card border-0 shadow-sm rounded-4 h-100 event-card"
                        onClick={() => openEventModal(event)}
                        style={{ cursor: 'pointer' }}
                      >
                      {event.cover_image
                        ? (
                          <img
                            src={event.cover_image}
                            alt={event.title}
                            className="card-img-top"
                            style={{ height: '220px', objectFit: 'cover' }}
                          />
                        )
                        : (
                          <div
                            className="card-img-top"
                            style={{
                              height: '220px',
                              background: 'linear-gradient(135deg,#0b1c6b,#159EEC)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '3rem'
                            }}
                          >
                            ✝️
                          </div>
                        )
                      }
                        <div className="card-body p-4">
                          <h5 className="card-title fw-bold text-primary mb-2">{event.title}</h5>
                          
                          {/* Status Badge */}
                          <div className="mb-3">
                            <span className={`badge ${getStatusBadgeClass(event.status_display)} px-3 py-2`}>
                              <i className="bi bi-calendar-check me-1"></i>
                              {event.status_display}
                            </span>
                          </div>
                          
                          <div className="mb-2">
                            <i className="bi bi-calendar3 text-primary me-2"></i>
                            <span className="text-muted">
                              {formatDate(event.start_date)}
                              {event.end_date && event.end_date !== event.start_date && 
                                ` - ${formatDate(event.end_date)}`}
                            </span>
                          </div>

                          {event.start_time && (
                            <div className="mb-2">
                              <i className="bi bi-clock text-primary me-2"></i>
                              <span className="text-muted">
                                {formatTime(event.start_time)}
                                {event.end_time && ` - ${formatTime(event.end_time)}`}
                              </span>
                            </div>
                          )}

                          <div className="mb-3">
                            <i className="bi bi-geo-alt text-primary me-2"></i>
                            <span className="text-muted small">{event.location_address}</span>
                          </div>

                          <p className="card-text text-muted mb-3" style={{ fontSize: '14px' }}>
                            {details.slice(0, 120)}...
                          </p>

                          <button className="btn btn-outline-primary rounded-pill w-100">
                            <i className="bi bi-eye me-2"></i>View Details
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* EVENT DETAILS MODAL */}
        {showEventModal && selectedEvent && (
          <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
              <div className="modal-content border-0 shadow-lg rounded-4">
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold text-primary">
                    <i className="bi bi-calendar-event me-2"></i>
                    Event Details
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={closeEventModal}
                  ></button>
                </div>
                <div className="modal-body p-0">
                  {/* Event Cover Image */}
                  <img 
                    src={selectedEvent.cover_image} 
                    alt={selectedEvent.title}
                    className="w-100"
                    style={{ height: '300px', objectFit: 'cover' }}
                  />

                  <div className="p-4">
                    {/* Event Title */}
                    <h2 className="fw-bold text-primary mb-2">{selectedEvent.title}</h2>

                    {/* Status Badge */}
                    <div className="mb-4">
                      <span className={`badge ${getStatusBadgeClass(selectedEvent.status_display)} px-4 py-2 fs-6`}>
                        <i className="bi bi-calendar-check me-1"></i>
                        {selectedEvent.status_display}
                      </span>
                    </div>

                    {/* Event Info Grid */}
                    <div className="row g-3 mb-4">
                      <div className="col-md-6">
                        <div className="d-flex align-items-start">
                          <i className="bi bi-calendar3 text-primary fs-5 me-3 mt-1"></i>
                          <div>
                            <h6 className="fw-bold mb-1">Date</h6>
                            <p className="mb-0 text-muted">
                              {formatDate(selectedEvent.start_date)}
                              {selectedEvent.end_date && selectedEvent.end_date !== selectedEvent.start_date && 
                                ` - ${formatDate(selectedEvent.end_date)}`}
                            </p>
                          </div>
                        </div>
                      </div>

                      {selectedEvent.start_time && (
                        <div className="col-md-6">
                          <div className="d-flex align-items-start">
                            <i className="bi bi-clock text-primary fs-5 me-3 mt-1"></i>
                            <div>
                              <h6 className="fw-bold mb-1">Time</h6>
                              <p className="mb-0 text-muted">
                                {formatTime(selectedEvent.start_time)}
                                {selectedEvent.end_time && ` - ${formatTime(selectedEvent.end_time)}`}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="col-12">
                        <div className="d-flex align-items-start">
                          <i className="bi bi-geo-alt text-primary fs-5 me-3 mt-1"></i>
                          <div>
                            <h6 className="fw-bold mb-1">Location</h6>
                            <p className="mb-0 text-muted">{selectedEvent.location_address}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Event Details */}
                    <div className="mb-4">
                      <h6 className="fw-bold mb-3">
                        <i className="bi bi-info-circle text-primary me-2"></i>
                        About This Event
                      </h6>
                      <p className="text-muted lh-lg">{extractDetailsText(selectedEvent.detailsJson)}</p>
                    </div>

                    {/* Published Date */}
                    <div className="text-muted small">
                      <i className="bi bi-megaphone me-1"></i>
                      Published: {formatDate(selectedEvent.pub_date)}
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0 pt-0 px-4 pb-4">
                  <button className="btn btn-outline-secondary rounded-pill px-4" onClick={closeEventModal}>
                    Close
                  </button>
                  {selectedEvent.status_display !== "Finished" && (
                    <button className="btn btn-primary rounded-pill px-4">
                      <i className="bi bi-calendar-check me-2"></i>
                      Register / RSVP
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

 
        {/* SOCIAL */}
        <section className="social-section py-5 text-center text-white">
          <div className="container py-4">
            <h2 className="display-5 fw-bold mb-3">Follow Us</h2>
            <p className="fs-6 mb-4 mx-auto" style={{ maxWidth: '600px' }}>
              Stay connected with Gospel City Church International through our social platforms.
            </p>
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <a href="https://www.linkedin.com/company/gospel-city-church-international/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-linkedin fs-4"></i>
              </a>
              <a href="https://groups.imo.im/.5l9akqTOrYa5Tv4T4" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
            <i className="bi bi-chat-dots fs-4"></i>
            </a>
              <a href="https://x.com/GospelCityChur?t=x8dsFbFkghmYh02lV0FVxQ&s=03" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-twitter-x fs-4"></i>
              </a>
              <a href="https://whatsapp.com/channel/0029Vb769UyDzgTBAZZcb40F" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-whatsapp fs-4"></i>
              </a>
              <a href="https://youtube.com/@gospelcitychurchinternational?si=1pZx2ak4ss3b4HuU" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-youtube fs-4"></i>
              </a>
            </div>
          </div>
        </section>

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500;600;700;800&display=swap');

          * {
            font-family: 'Lexend', sans-serif;
          }

          /* HERO */
          .events-hero {
            position: relative;
            background-image: url('/images/y2.jpg');
            background-size: cover;
            background-position: center;
            min-height: 500px;
            display: flex;
            align-items: center;
          }

          .hero-overlay {
            position: relative;
            width: 100%;
            z-index: 2;
            padding: 80px 0;
          }

          .events-hero::before {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(135deg, rgba(11,28,107,0.95), rgba(21,158,236,0.85));
            z-index: 1;
          }

          .search-container .form-control {
            padding: 14px 20px;
            font-size: 16px;
          }

          .search-container .form-control:focus {
            box-shadow: 0 0 0 0.25rem rgba(26,58,161,0.25);
          }

          .search-container .input-group-text {
            padding: 14px 20px;
          }

          /* EVENT CARDS */
          .event-card {
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }

          .event-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 20px 50px rgba(26,58,161,0.2) !important;
          }

          /* MODAL */
          .modal {
            animation: fadeIn 0.3s ease;
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          /* COLORS */
          .text-primary {
            color: #1a3aa1 !important;
          }

          .text-info {
            color: #159EEC !important;
          }

          .bg-info {
            background-color: #159EEC !important;
          }

          .bg-primary {
            background-color: #0b1c6b !important;
          }

          .btn-primary {
            background: #0b1c6b;
            border-color: #0b1c6b;
          }

          .btn-primary:hover {
            background: #159EEC;
            border-color: #159EEC;
          }

          .btn-outline-primary {
            color: #0b1c6b;
            border-color: #0b1c6b;
          }

          .btn-outline-primary:hover {
            background: #0b1c6b;
            border-color: #0b1c6b;
            color: #fff;
          }

          /* SOCIAL */
          .social-section {
            background: linear-gradient(135deg,#0b1c6b,#159EEC);
          }

          .social-icon {
            width: 52px;
            height: 52px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s;
          }

          .social-icon:hover {
            background: rgba(255,255,255,0.3);
            transform: translateY(-4px) scale(1.1);
          }

          @media (max-width: 768px) {
            .events-hero {
              min-height: 400px;
            }

            .display-3 {
              font-size: 2.5rem;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default Events;